import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  UrgentConsulting, 
  CreateUrgentConsultingRequest, 
  UpdateUrgentConsultingRequest,
  AuthCredentials,
  AuthResponse,
  ApiError 
} from '../types/api';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Use environment variable or default to local development
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear token and redirect to login
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(this.formatError(error));
      }
    );
  }

  private formatError(error: any): ApiError {
    if (error.response) {
      return {
        message: error.response.data?.message || error.response.data?.detail || 'An error occurred',
        status: error.response.status,
        details: error.response.data,
      };
    } else if (error.request) {
      return {
        message: 'Network error - please check your connection',
        status: 0,
        details: error.request,
      };
    } else {
      return {
        message: error.message || 'An unexpected error occurred',
        status: -1,
        details: error,
      };
    }
  }

  // Authentication
  async login(credentials: AuthCredentials): Promise<AuthResponse> {
    const formData = new FormData();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response: AxiosResponse<AuthResponse> = await this.api.post('/user_login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Store token and user info
    localStorage.setItem('auth_token', response.data.access_token);
    localStorage.setItem('user', JSON.stringify(response.data.user));

    return response.data;
  }

  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }

  async initGoogleLogin(redirectUri: string): Promise<string> {
    const response: AxiosResponse<{ url: string }> = await this.api.get(
      `/google_login_init?redirect_uri=${encodeURIComponent(redirectUri)}`
    );
    return response.data.url;
  }

  async completeGoogleLogin(code: string, redirectUri: string): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.get(
      `/google_login?code=${encodeURIComponent(code)}&redirect_uri=${encodeURIComponent(redirectUri)}`
    );

    // Store token and user info (same as regular login)
    localStorage.setItem('auth_token', response.data.access_token);
    localStorage.setItem('user', JSON.stringify(response.data.user));

    return response.data;
  }

  getCurrentUser(): any {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  // Urgent Consulting API
  async getAllUrgentConsultations(limit?: number, offset?: number): Promise<UrgentConsulting[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());

    const response: AxiosResponse<UrgentConsulting[]> = await this.api.get(
      `/urgent_consulting${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  }

  async getOpenUrgentConsultations(): Promise<UrgentConsulting[]> {
    const response: AxiosResponse<UrgentConsulting[]> = await this.api.get('/urgent_consulting_open');
    return response.data;
  }

  async getDoneUrgentConsultations(): Promise<UrgentConsulting[]> {
    const response: AxiosResponse<UrgentConsulting[]> = await this.api.get('/urgent_consulting_done');
    return response.data;
  }

  async getNeedPatientMeetingUrgentConsultations(): Promise<UrgentConsulting[]> {
    const response: AxiosResponse<UrgentConsulting[]> = await this.api.get('/urgent_consulting_need_patient_meeting_list');
    return response.data;
  }

  async getUrgentConsultingById(id: number): Promise<UrgentConsulting> {
    const response: AxiosResponse<UrgentConsulting> = await this.api.get(`/urgent_consulting/${id}`);
    return response.data;
  }

  async createUrgentConsulting(data: CreateUrgentConsultingRequest): Promise<UrgentConsulting> {
    const response: AxiosResponse<UrgentConsulting> = await this.api.post('/urgent_consulting', data);
    return response.data;
  }

  async updateUrgentConsulting(id: number, data: UpdateUrgentConsultingRequest): Promise<UrgentConsulting> {
    const response: AxiosResponse<UrgentConsulting> = await this.api.put(`/urgent_consulting/${id}`, data);
    return response.data;
  }

  // Urgent Consulting Actions
  async needPatientMeeting(callId: number): Promise<string> {
    const response: AxiosResponse<string> = await this.api.get(
      `/urgent_consulting_need_patient_meeting?call_id=${callId}`
    );
    return response.data;
  }

  async makeConferenceCall(callId: number): Promise<string> {
    const response: AxiosResponse<string> = await this.api.get(
      `/urgent_consulting_make_con_call_auth?call_id=${callId}`
    );
    return response.data;
  }

  async conferenceCallDone(callId: number): Promise<string> {
    const response: AxiosResponse<string> = await this.api.get(
      `/urgent_consulting_con_call_done?call_id=${callId}`
    );
    return response.data;
  }
}

export const apiService = new ApiService();